import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import apiClient from '../lib/apiClient';

const AuthContext = createContext(null);

const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
};

const getValidToken = () => {
  const t = localStorage.getItem('xhotel_token');
  if (!t || isTokenExpired(t)) {
    localStorage.removeItem('xhotel_token');
    localStorage.removeItem('xhotel_user');
    return null;
  }
  return t;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => getValidToken());
  const [user, setUser] = useState(() => {
    if (!getValidToken()) return null;
    try { return JSON.parse(localStorage.getItem('xhotel_user')); } catch { return null; }
  });

  // Sync auth state when localStorage changes in another tab
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'xhotel_token') {
        setToken(e.newValue || null);
        if (!e.newValue) setUser(null);
      }
      if (e.key === 'xhotel_user') {
        try { setUser(e.newValue ? JSON.parse(e.newValue) : null); } catch { setUser(null); }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const login = useCallback(async (userId, password) => {
    const data = await apiClient.post('/api/User/GuestLogin', { userId, password });
    localStorage.setItem('xhotel_token', data.token);
    const userInfo = { userId, name: data.userName || userId, role: data.role };
    localStorage.setItem('xhotel_user', JSON.stringify(userInfo));
    setToken(data.token);
    setUser(userInfo);
    return data;
  }, []);

  const register = useCallback(async (form) => {
    return await apiClient.post('/api/User/AddGuest', { ...form, role: 5 });
  }, []);

  const setPassword = useCallback(async (email, otp, newPassword) => {
    return await apiClient.post('/api/User/SetPassword', { email, otp, newPassword });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('xhotel_token');
    localStorage.removeItem('xhotel_user');
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ token, user, isLoggedIn: !!token, login, logout, register, setPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
